"use server";

import { cookies, headers } from "next/headers";
import { redirect } from "next/navigation";
import { createSession, findOrCreateUser, mergeDeviceIntoUser, normalizePhone } from "@/lib/auth";
import { getDb } from "@/lib/db/client";
import { getLang } from "@/lib/lang";
import { dropChallenge, OTP, startChallenge, verifyChallenge } from "@/lib/otp";
import { clientIp } from "@/lib/rate-limit";
import { DEVICE_COOKIE } from "@/lib/receipts";
import { SESSION_COOKIE, safeNext } from "@/lib/session";
import { otpMessage, sendSms, smsProvider } from "@/lib/sms";

export type SendResult =
  | {
      ok: true;
      phone: string;
      /** seconds until another code can be sent */
      resendIn: number;
      /** only with SMS_PROVIDER=dev, where no text goes out */
      devCode?: string;
    }
  | { ok: false; error: "invalid_phone" | "rate_limited" | "send_failed" }
  | { ok: false; error: "cooldown"; retryIn: number; phone: string };

export async function sendCode(input: string): Promise<SendResult> {
  const phone = normalizePhone(input);
  if (!phone) return { ok: false, error: "invalid_phone" };

  const db = getDb();
  const started = startChallenge(db, phone, clientIp(await headers()));
  // the form uses phone to tell a quick re-send of the same number, typed any way
  if (!started.ok) return started.error === "cooldown" ? { ...started, phone } : started;

  try {
    await sendSms(phone, otpMessage(started.code, await getLang()));
  } catch (err) {
    console.error(`sms to ${phone} failed:`, err);
    dropChallenge(db, started.challengeId);
    return { ok: false, error: "send_failed" };
  }
  const resendIn = OTP.resendMs / 1000;
  return smsProvider() === "dev" ? { ok: true, phone, resendIn, devCode: started.code } : { ok: true, phone, resendIn };
}

export type VerifyError = "invalid_phone" | "bad_code" | "expired" | "too_many_attempts" | "rate_limited";

export async function verifyCode(input: string, code: string, next?: string): Promise<{ ok: false; error: VerifyError }> {
  // the phone comes back from the browser, so it gets checked again here
  const phone = normalizePhone(input);
  if (!phone) return { ok: false, error: "invalid_phone" };

  const db = getDb();
  const checked = verifyChallenge(db, phone, code, clientIp(await headers()));
  if (!checked.ok) return checked;

  const jar = await cookies();
  const user = findOrCreateUser(db, phone);
  const { token, expiresAt } = createSession(db, user.id);

  jar.set(SESSION_COOKIE, token, {
    httpOnly: true,
    secure: process.env.NODE_ENV === "production",
    sameSite: "lax",
    path: "/",
    expires: new Date(expiresAt),
  });
  mergeDeviceIntoUser(db, jar.get(DEVICE_COOKIE)?.value, user.id);

  redirect(safeNext(next));
}
